import { useEffect, useState } from 'react';
// components/ThemeToggle.tsx
import { Button, Divider, Select, SegmentedControl, Text, Tooltip } from '@/components/ui';
import { useTheme } from '@/hooks/useTheme.ts';
import { IconDeviceDesktop, IconMoon, IconSun } from '@tabler/icons-react';
import clsx from 'clsx';

export type ThemeMode = 'light' | 'dark' | 'system';
export type InterfaceTheme = 'classic' | 'modern';

interface ThemeToggleProps {
  variant?: 'button' | 'select' | 'settings' | 'segmented';
  showSystem?: boolean;
  showInterfaceTheme?: boolean;
  className?: string;
}

export function ThemeToggle({
  variant = 'button',
  showSystem = false,
  showInterfaceTheme = false,
  className,
}: ThemeToggleProps) {
  const { theme, setTheme, mounted, resolvedTheme } = useTheme();
  const [interfaceTheme, setInterfaceTheme] =
    useState<InterfaceTheme>('classic');

  // Initialize interfaceTheme state from localStorage (for display purposes only)
  // Actual initialization is handled by ThemeInitializer component
  useEffect(() => {
    if (typeof window !== 'undefined' && showInterfaceTheme) {
      const savedInterfaceTheme = localStorage.getItem(
        'interface-theme'
      ) as InterfaceTheme;
      if (savedInterfaceTheme) {
        setInterfaceTheme(savedInterfaceTheme);
      }
    }
  }, [showInterfaceTheme]);

  if (!mounted) return null;

  const current = resolvedTheme;

  // Handle interface theme change
  const handleInterfaceThemeChange = (newTheme: InterfaceTheme) => {
    setInterfaceTheme(newTheme);
    // Add/remove theme class to body
    if (typeof window !== 'undefined') {
      document.body.classList.remove('theme-classic', 'theme-modern');
      document.body.classList.add(`theme-${newTheme}`);
      // Save to localStorage
      localStorage.setItem('interface-theme', newTheme);
    }
  };

  // Segmented variant for user menu
  if (variant === 'segmented') {
    const themeItems = [
      {
        value: 'light',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSun size={16} />
            <span>Light</span>
          </div>
        ),
      },
      {
        value: 'dark',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconMoon size={16} />
            <span>Dark</span>
          </div>
        ),
      },
      {
        value: 'system',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconDeviceDesktop size={16} />
            <span>System</span>
          </div>
        ),
      },
    ];

    return (
      <SegmentedControl
        items={themeItems}
        value={theme || 'system'}
        onValueChange={(value: string) => setTheme(value as ThemeMode)}
        size="small"
        fullWidth
        className={clsx('theme-toggle-segmented', className)}
      />
    );
  }

  // Button variant (original)
  if (variant === 'button') {
    return (
      <Tooltip
        content={`Switch to ${current === 'light' ? 'dark' : 'light'} mode`}
      >
        <Button
          color="secondary"
          isIconOnly
          size="small"
          onClick={() => setTheme(current === 'light' ? 'dark' : 'light')}
          variant="text"
          className={clsx('theme-toggle hover-effect', className)}
        >
          {current === 'light' ? <IconMoon /> : <IconSun />}
        </Button>
      </Tooltip>
    );
  }

  // Select variant for settings page
  if (variant === 'select') {
    const colorThemeOptions = [
      { value: 'light', label: 'Light', startIcon: <IconSun /> },
      { value: 'dark', label: 'Dark', startIcon: <IconMoon /> },
      ...(showSystem
        ? [
            {
              value: 'system',
              label: 'System',
              startIcon: <IconDeviceDesktop />,
            },
          ]
        : []),
    ];

    const interfaceThemeOptions = [
      { value: 'classic', label: 'Classic' },
      { value: 'modern', label: 'Modern' },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <Text weight="medium" style={{ marginBottom: '0.5rem' }}>
            Color Theme
          </Text>
          <Select
            value={theme || 'system'}
            onValueChange={(value: string) => setTheme(value as ThemeMode)}
            options={colorThemeOptions}
            placeholder="Select theme"
          />
        </div>

        {showInterfaceTheme && (
          <div>
            <Text weight="medium" style={{ marginBottom: '0.5rem' }}>
              Interface Theme
            </Text>
            <Select
              value={interfaceTheme}
              onValueChange={(value: string) =>
                handleInterfaceThemeChange(value as InterfaceTheme)
              }
              options={interfaceThemeOptions}
              placeholder="Select interface theme"
            />
          </div>
        )}
      </div>
    );
  }

  // Settings variant (inline switches)
  if (variant === 'settings') {
    const colorThemeOptions = [
      { value: 'light', label: 'Light' },
      { value: 'dark', label: 'Dark' },
      ...(showSystem ? [{ value: 'system', label: 'System' }] : []),
    ];

    const interfaceThemeOptions = [
      { value: 'classic', label: 'Classic' },
      { value: 'modern', label: 'Modern' },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Text weight="medium">Color Theme</Text>
            <Text size="3xsmall" color="secondary">
              Choose between light, dark, or system theme
            </Text>
          </div>
          <div style={{ minWidth: 120 }}>
            <Select
              value={theme || 'system'}
              onValueChange={(value: string) => setTheme(value as ThemeMode)}
              options={colorThemeOptions}
            />
          </div>
        </div>

        {showInterfaceTheme && (
          <>
            <Divider />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <Text weight="medium">Interface Theme</Text>
                <Text size="3xsmall" color="secondary">
                  Choose between classic and modern interface styles
                </Text>
              </div>
              <div style={{ minWidth: 120 }}>
                <Select
                  value={interfaceTheme}
                  onValueChange={(value: string) =>
                    handleInterfaceThemeChange(value as InterfaceTheme)
                  }
                  options={interfaceThemeOptions}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}
